import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

const LAST_DAY = 'LAST';
type DayValue = number | typeof LAST_DAY;

export interface BulkDateField {
  /** 폼 필드 key (예: 'issueDay', 'depositDay') */
  key: string;
  /** UI 라벨 (예: '세금 발행일', '입금 예정일') */
  label: string;
  /** 기본 실행일 (1~31 또는 'LAST') */
  defaultDay?: DayValue;
}

/** 한 행에 들어갈 timestamp 매핑 (key → timestamp) */
export type BulkGeneratedRow = Record<string, number>;

export interface BulkRegisterDialogProps {
  open: boolean;
  title?: string;
  /** 일괄 등록할 날짜 컬럼 정의 (1개 이상) */
  dateFields: BulkDateField[];
  /** 추가 입력값 영역 (각 섹션별 기본값 입력). 함수로 넘기면 현재 미리보기 행을 받아 동적 렌더 가능 */
  children?: ReactNode | ((ctx: { previewRows: BulkGeneratedRow[] }) => ReactNode);
  onClose: () => void;
  /** 생성된 행(각 행은 dateFields 각 key의 timestamp 포함) 전달 */
  onConfirm: (rows: BulkGeneratedRow[]) => void;
}

/**
 * 시작일~종료일 사이에 포함되는 각 월에 대해 행을 1건씩 생성.
 * 각 행은 dateFields 각 key 별로 지정된 일자(또는 말일)의 timestamp를 가짐.
 */
const generateRows = (
  start: Dayjs,
  end: Dayjs,
  fields: { key: string; day: DayValue }[],
): BulkGeneratedRow[] => {
  if (start.isAfter(end)) return [];
  const rows: BulkGeneratedRow[] = [];
  let cursor = start.startOf('month');
  const endMonth = end.startOf('month');
  while (cursor.isBefore(endMonth) || cursor.isSame(endMonth, 'month')) {
    const row: BulkGeneratedRow = {};
    fields.forEach(({ key, day }) => {
      const lastDayOfMonth = cursor.daysInMonth();
      const targetDay = day === LAST_DAY ? lastDayOfMonth : Math.min(day, lastDayOfMonth);
      row[key] = cursor.date(targetDay).startOf('day').valueOf();
    });
    rows.push(row);
    cursor = cursor.add(1, 'month');
  }
  return rows;
};

function BulkRegisterDialog({
  open,
  title = '일괄 등록',
  dateFields,
  children,
  onClose,
  onConfirm,
}: BulkRegisterDialogProps) {
  const today = useMemo(() => dayjs(), []);
  const [startDate, setStartDate] = useState<Dayjs | null>(today);
  const [endDate, setEndDate] = useState<Dayjs | null>(today.add(12, 'month'));
  const [dayByKey, setDayByKey] = useState<Record<string, DayValue>>({});
  // 사용자가 자유롭게 편집할 수 있도록 숫자 입력의 문자열 버퍼 별도 관리
  const [dayInputByKey, setDayInputByKey] = useState<Record<string, string>>({});

  // 다이얼로그가 새로 열릴 때 기본값 리셋 (dateFields 참조 변경으로 인한 재실행 방지를 위해 open 전이에만 반응)
  useEffect(() => {
    if (!open) return;
    setStartDate(today);
    setEndDate(today.add(11, 'month'));
    const nextDayByKey = dateFields.reduce<Record<string, DayValue>>((acc, f) => {
      acc[f.key] = f.defaultDay ?? today.date();
      return acc;
    }, {});
    setDayByKey(nextDayByKey);
    setDayInputByKey(
      dateFields.reduce<Record<string, string>>((acc, f) => {
        const d = nextDayByKey[f.key];
        acc[f.key] = typeof d === 'number' ? String(d) : String(today.date());
        return acc;
      }, {}),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewRows = useMemo(() => {
    if (!startDate || !endDate) return [];
    return generateRows(
      startDate,
      endDate,
      dateFields.map((f) => ({ key: f.key, day: dayByKey[f.key] ?? f.defaultDay ?? 1 })),
    );
  }, [startDate, endDate, dateFields, dayByKey]);

  const handleConfirm = () => {
    onConfirm(previewRows);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 3 }}>
          <Stack direction="row" spacing={2}>
            <DatePicker
              label="시작월"
              value={startDate}
              onChange={(v) => setStartDate(v)}
              views={['year', 'month']}
              format="YYYY-MM"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="종료월"
              value={endDate}
              onChange={(v) => setEndDate(v)}
              views={['year', 'month']}
              format="YYYY-MM"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Stack>

          {dateFields.map((f) => {
            const current = dayByKey[f.key] ?? f.defaultDay ?? LAST_DAY;
            const isLast = current === LAST_DAY;
            const numericDay = typeof current === 'number' ? current : 1;
            return (
              <Stack
                key={f.key}
                spacing={0.5}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" sx={{ width: 180, flexShrink: 0, fontWeight: 600 }}>
                    {f.label}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={isLast}
                          onChange={(e) =>
                            setDayByKey((prev) => ({
                              ...prev,
                              [f.key]: e.target.checked ? LAST_DAY : numericDay,
                            }))
                          }
                        />
                      }
                      label="매월 말일"
                    />
                    <TextField
                      size="small"
                      disabled={isLast}
                      value={isLast ? '' : dayInputByKey[f.key] ?? String(numericDay)}
                      onChange={(e) => {
                        const v = e.target.value;
                        // 숫자만 허용, 빈 값도 편집 중에는 허용
                        if (v !== '' && !/^\d{1,2}$/.test(v)) return;
                        setDayInputByKey((prev) => ({ ...prev, [f.key]: v }));
                        if (v === '') return;
                        const n = Number(v);
                        if (n >= 1 && n <= 31) {
                          setDayByKey((prev) => ({ ...prev, [f.key]: n }));
                        }
                      }}
                      onBlur={() => {
                        const v = dayInputByKey[f.key];
                        const n = Number(v);
                        // 유효하지 않으면 마지막으로 유효했던 값으로 복구
                        if (!v || isNaN(n) || n < 1) {
                          setDayInputByKey((prev) => ({ ...prev, [f.key]: String(numericDay) }));
                          return;
                        }
                        const clamped = Math.min(31, n);
                        setDayByKey((prev) => ({ ...prev, [f.key]: clamped }));
                        setDayInputByKey((prev) => ({ ...prev, [f.key]: String(clamped) }));
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">매월</InputAdornment>,
                        endAdornment: <InputAdornment position="end">일</InputAdornment>,
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ width: 160 }}
                    />
                  </Stack>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  해당 월에 일자가 없으면 말일로 보정됩니다.
                </Typography>
              </Stack>
            );
          })}
          {typeof children === 'function' ? children({ previewRows }) : children}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={previewRows.length === 0}>
          {previewRows.length}건 등록
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkRegisterDialog;
